import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../lib/api";

const UserPreferencesContext = createContext(null);

const DEFAULTS = {
  timezone: "Asia/Kolkata",
  date_format: "DD-MM-YYYY",
  time_format: "12h",
  number_format: "en-IN",
  currency: "INR",
  page_size: 25,
  theme: "system",
  sidebar_collapsed: false,
  compact_mode: false,
  notifications_enabled: true,
  email_digest: "daily",
  language: "en",
  start_page: "/",
};

// Date formatting map
const DATE_FORMAT_MAP = {
  "DD-MM-YYYY": { day: "2-digit", month: "2-digit", year: "numeric" },
  "MM-DD-YYYY": { month: "2-digit", day: "2-digit", year: "numeric" },
  "YYYY-MM-DD": { year: "numeric", month: "2-digit", day: "2-digit" },
  "DD/MM/YYYY": { day: "2-digit", month: "2-digit", year: "numeric" },
  "MM/DD/YYYY": { month: "2-digit", day: "2-digit", year: "numeric" },
  "DD MMM YYYY": { day: "2-digit", month: "short", year: "numeric" },
};

export function UserPreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const fetchPrefs = useCallback(async () => {
    try {
      const token = localStorage.getItem("stancor_token");
      if (!token) return;
      const res = await api.get("/user-preferences");
      setPrefs({ ...DEFAULTS, ...res.data });
    } catch {
      // Use defaults
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { fetchPrefs(); }, [fetchPrefs]);

  const updatePrefs = async (updates) => {
    const res = await api.put("/user-preferences", updates);
    setPrefs({ ...DEFAULTS, ...res.data });
    return res.data;
  };

  // Format a date string according to user preference
  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return "—";
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const fmt = prefs.date_format || "DD-MM-YYYY";
      const opts = DATE_FORMAT_MAP[fmt] || DATE_FORMAT_MAP["DD-MM-YYYY"];
      const locale = prefs.number_format || "en-IN";

      // For DD-MM-YYYY and DD/MM/YYYY, we need custom formatting
      if (fmt === "DD-MM-YYYY") {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        return `${d}-${m}-${date.getFullYear()}`;
      }
      if (fmt === "DD/MM/YYYY") {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        return `${d}/${m}/${date.getFullYear()}`;
      }
      if (fmt === "MM-DD-YYYY") {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        return `${m}-${d}-${date.getFullYear()}`;
      }
      if (fmt === "MM/DD/YYYY") {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        return `${m}/${d}/${date.getFullYear()}`;
      }
      if (fmt === "YYYY-MM-DD") {
        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        return `${date.getFullYear()}-${m}-${d}`;
      }
      return new Intl.DateTimeFormat(locale, opts).format(date);
    } catch {
      return dateStr;
    }
  }, [prefs.date_format, prefs.number_format]);

  // Format a number according to user preference
  const formatNumber = useCallback((num, decimals = 2) => {
    if (num == null || isNaN(num)) return "—";
    try {
      return new Intl.NumberFormat(prefs.number_format || "en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
    } catch {
      return String(num);
    }
  }, [prefs.number_format]);

  // Format a currency value
  const formatCurrency = useCallback((amount) => {
    if (amount == null || isNaN(amount)) return "—";
    try {
      return new Intl.NumberFormat(prefs.number_format || "en-IN", {
        style: "currency",
        currency: prefs.currency || "INR",
      }).format(amount);
    } catch {
      return String(amount);
    }
  }, [prefs.number_format, prefs.currency]);

  return (
    <UserPreferencesContext.Provider value={{
      prefs, loaded, updatePrefs, fetchPrefs,
      formatDate, formatNumber, formatCurrency,
    }}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export const useUserPreferences = () => {
  const ctx = useContext(UserPreferencesContext);
  if (!ctx) throw new Error("useUserPreferences must be used within UserPreferencesProvider");
  return ctx;
};
